// 다음(카카오) 우편번호 서비스 로더.
// 도로명/지번 주소 검색 UI 를 앱 내 레이어(임베드)로 띄워 선택 결과(도로명 주소 + 우편번호)를 반환한다.
//  - 무료이고 별도 API 키/도메인 등록이 필요 없다 (외부 스크립트만 동적 로드).
//  - 모바일 대응(개선항목 15): 과거 .open() 팝업 방식은 await(스크립트 로드)로 사용자 제스처 컨텍스트가
//    끊겨 모바일 브라우저에서 팝업이 차단됐다. .embed() 로 화면 내 오버레이에 렌더해 팝업 없이 동작하게 한다.
//  - 정식(Capacitor) 빌드에서는 외부 스크립트 로드 정책을 별도 검토해야 한다 (PWA 테스트 환경 우선).

const SCRIPT_SRC = "https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";

// window.daum.Postcode 최소 타입 (any 회피용).
interface DaumPostcodeData {
  roadAddress: string;
  jibunAddress: string;
  zonecode: string;
}
interface DaumPostcodeInstance {
  open: () => void;
  // 지정한 DOM 요소 안에 검색 UI 를 렌더한다(팝업 대신 임베드). autoClose 시 선택 후 자동 정리.
  embed: (el: HTMLElement, opts?: { autoClose?: boolean }) => void;
}
interface DaumPostcodeCtor {
  new (opts: {
    oncomplete: (data: DaumPostcodeData) => void;
    onclose?: (state: string) => void;
    // 임베드 영역을 컨테이너에 꽉 채우기 위한 크기 지정.
    width?: string | number;
    height?: string | number;
  }): DaumPostcodeInstance;
}
interface DaumNamespace {
  Postcode: DaumPostcodeCtor;
}

function getDaum(): DaumNamespace | null {
  const w = window as unknown as { daum?: DaumNamespace };
  return w.daum ?? null;
}

let loadPromise: Promise<void> | null = null;

// 스크립트를 1회만 로드한다. 이미 로드돼 있으면 즉시 resolve.
function loadScript(): Promise<void> {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(new Error("브라우저 환경이 아닙니다."));
  }
  if (getDaum()?.Postcode) {
    return Promise.resolve();
  }
  if (loadPromise) {
    return loadPromise;
  }
  loadPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      loadPromise = null;
      reject(new Error("우편번호 서비스를 불러오지 못했습니다."));
    };
    document.head.appendChild(script);
  });
  return loadPromise;
}

export interface PostcodeResult {
  roadAddress: string;
  jibunAddress: string;
  zonecode: string;
}

// 우편번호 검색 UI 를 앱 내 오버레이로 연다.
//  - 사용자가 주소를 선택하면 그 결과를, 닫기/배경 클릭/취소 시 null 을 resolve 한다.
//  - 팝업(window.open)을 쓰지 않으므로 모바일에서도 안정적으로 동작한다(개선항목 15).
export async function openPostcodeSearch(): Promise<PostcodeResult | null> {
  await loadScript();
  const daum = getDaum();
  if (!daum) {
    throw new Error("우편번호 서비스를 사용할 수 없습니다.");
  }

  return new Promise<PostcodeResult | null>((resolve) => {
    let settled = false;

    // 전체화면 오버레이 + 중앙 패널. 유틸이라 Tailwind 대신 인라인 스타일로 구성한다.
    const overlay = document.createElement("div");
    overlay.style.cssText =
      "position:fixed;inset:0;z-index:1000;background:rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;padding:16px;";

    const panel = document.createElement("div");
    panel.style.cssText =
      "display:flex;flex-direction:column;width:100%;max-width:480px;height:80vh;max-height:600px;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 10px 40px rgba(0,0,0,0.25);";

    // 상단 닫기 바.
    const bar = document.createElement("div");
    bar.style.cssText =
      "display:flex;align-items:center;justify-content:space-between;padding:8px 12px;border-bottom:1px solid #eee;";
    const titleEl = document.createElement("span");
    titleEl.textContent = "주소 검색";
    titleEl.style.cssText = "font-size:14px;font-weight:700;color:#1f2937;";
    const closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.textContent = "닫기";
    closeBtn.setAttribute("aria-label", "주소 검색 닫기");
    closeBtn.style.cssText =
      "min-height:40px;padding:0 12px;border:1px solid #d1d5db;border-radius:8px;background:#fff;font-size:14px;color:#374151;cursor:pointer;";
    bar.appendChild(titleEl);
    bar.appendChild(closeBtn);

    // 임베드 영역(남은 공간을 채운다).
    const host = document.createElement("div");
    host.style.cssText = "flex:1;min-height:0;";

    panel.appendChild(bar);
    panel.appendChild(host);
    overlay.appendChild(panel);
    document.body.appendChild(overlay);

    const finish = (result: PostcodeResult | null) => {
      if (settled) {
        return;
      }
      settled = true;
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
      resolve(result);
    };

    // 닫기 버튼 / 배경 클릭은 취소(null).
    closeBtn.addEventListener("click", () => finish(null));
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) {
        finish(null);
      }
    });

    new daum.Postcode({
      oncomplete: (data) =>
        finish({
          roadAddress: data.roadAddress,
          jibunAddress: data.jibunAddress,
          zonecode: data.zonecode,
        }),
      // 임베드 내부에서 닫힌 경우(미선택)도 null 로 종료한다. 완료 후 호출은 settled 가드로 무시.
      onclose: () => finish(null),
      width: "100%",
      height: "100%",
    }).embed(host, { autoClose: true });
  });
}
